<script setup lang="ts">
const { isSportsPage } = useSportsBook();
const { isCactusSportsbookEnabled } = useFeatureFlagsEnv();
const { userCanOnlyWithdraw } = useUserLimits();
const { bottomMenu } = storeToRefs(useLayoutStore());

const bottomMenuHeight = computed(() => {
  const bottomMenuHeight = bottomMenu.value.height;

  return bottomMenuHeight ? `${bottomMenuHeight + 10}px` : '10px';
});

const isHideButton = computed(() => {
  return isSportsPage.value && isCactusSportsbookEnabled.value;
});

const scrollToTop = (): void => {
  // Quem rola é a window (documento) em todas as páginas — home/cassino
  // passaram a rolar na window com o auto-hide da barra do Safari.
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
};

const isButtonVisible = ref(false);

const layoutStore = useLayoutStore();
const { isLayoutMobileOrTablet } = useLayout();

const centralizedCalc = computed(() => {
  if (isLayoutMobileOrTablet.value || userCanOnlyWithdraw.value) {
    return 'translateX(-50%)';
  }

  if (layoutStore.menuOpen) {
    return 'translateX(47.5%)';
  }

  return 'translateX(-25%)';
});

const checkScroll = () => {
  isButtonVisible.value = window.scrollY > 300;
};

const isModalOpen = ref(false);

const observer = new MutationObserver(() => {
  isModalOpen.value = document.body.classList.contains('modal-open');
});

onMounted(() => {
  if (import.meta.client) {
    window.addEventListener('scroll', checkScroll, { passive: true });
    checkScroll();
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });
    isModalOpen.value = document.body.classList.contains('modal-open');
  }
});

onUnmounted(() => {
  if (import.meta.client) {
    window.removeEventListener('scroll', checkScroll);
    observer.disconnect();
  }
});
</script>

<template>
  <div
    v-if="!isHideButton && !isModalOpen"
    :class="[
      $style.backToTop,
      {
        [$style.isVisible]: isButtonVisible,
        [$style.isSportsPage]: isSportsPage,
      },
    ]"
    @click="scrollToTop"
  >
    <NuxtIcon
      class="text-[36px] lg:text-[48px] pt-2 lg:pt-3"
      name="arrows/back-to-top"
    />
  </div>
</template>

<style module lang="postcss">
.backToTop {
  @apply fixed left-[50%] text-white cursor-pointer z-[50] opacity-0 select-none transition-all duration-300;
  display: flex;
  width: 36px;
  height: 36px;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  border-radius: 212px;
  border: 0.857px solid var(--colors-base-primary-400, #1414dd);
  background: var(--a-BLUE-BACKGROUND, #041a29);
  bottom: -60px;

  & :deep(span) {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
  }

  & :deep(svg) {
    width: 14px;
    height: 14px;
  }

  @media (width >= 1024px) {
    width: 48px;
    height: 48px;
    left: 88%;

    & :deep(svg) {
      width: 20px;
      height: 20px;
    }
  }

  @media (width < 1024px) {
    @apply left-auto;
    /* Escondido fica fora da tela só na vertical (bottom: -60px + opacity: 0).
       NÃO usar right negativo: como é position: fixed, vaza da viewport e gera
       scroll horizontal (o overflow-x: clip da raiz não contém elementos fixed). */
    right: 16px;
  }

  transform: v-bind(centralizedCalc);

  &:active {
    transform: v-bind(centralizedCalc) scale(0.95);
  }

  &.isVisible {
    bottom: v-bind(bottomMenuHeight);
    @apply lg:bottom-[20px] opacity-100;

    @media (width < 1024px) {
      right: 16px;
    }
  }

  &.isSportsPage {
    @apply bottom-28 lg:bottom-4;
  }
}
</style>
